import { nullishFilter, isNullish } from '@caresend/utils';
import { defineComponent, useSlots, ref, computed, h } from 'vue';
import { ButtonStyle, ButtonIconAnimation } from './model.js';
import __vue_component__ from '../../ui/Tooltip/Tooltip.vue.js';
import { watchOnce } from '../../../composables/dom.js';
import '@caresend/types';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import { getRoute } from '../../../router/helpers/initRouter.js';
import 'immutability-helper';
import '../../../router/routes/dev/model.js';
import '../../../router/routes/dev/routes.js';
import '../../../router/routes/dashboard/model.js';
import '../../../router/routes/pro/model.js';
import '../../../router/routes/visitsPage/model.js';
import '../../../router/routes/orderFlow/model.js';
import '../../../router/routes/orderFlow/privateHelpers.js';
import '../../../router/routes/orderFlow/routes.js';
import '../../../router/routes/schedulingFlow/model.js';
import '../../../router/routes/schedulingFlow/routes.js';
import '../../../router/routes/chartingFlow/model.js';
import '../../../router/routes/chartingFlow/routes.js';
import '../../../router/routes/portal/portalRoutes.js';
import '../../../router/routes/portal/model.js';
import '../../../router/routes/visitsPage/routes.js';

const controller = defineComponent({
  name: 'ButtonComponent',
  components: {
    Tooltip: __vue_component__
  },
  props: {
    /**
     * If tooltip is used, this prop will be passed along as
     * `alwaysAllowOnTouchDevices` to the Tooltip component.
     */
    alwaysAllowTooltipOnTouchDevices: Boolean,
    buttonStyle: {
      type: String,
      default: 'primary'
    },
    buttonSize: {
      type: String,
      default: 'md'
    },
    circular: Boolean,
    customStyles: {
      type: String,
      default: null
    },

    /**
     * Pass `true` to disable the button. Pass a string to disable the button
     * and show a tooltip on hover.
     */
    disabled: {
      type: [Boolean, String],
      default: false
    },
    fullWidth: Boolean,

    /** External URL for navigation upon click. Forces `target="_blank"` */
    href: {
      type: String,
      default: null
    },
    iconPack: {
      type: String,
      default: 'fasr'
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    iconAnimation: {
      type: String,
      default: undefined
    },
    loading: Boolean,
    nativeType: {
      type: String,
      default: 'button'
    },
    selected: Boolean,
    square: Boolean,

    /**
     * To be passed through to anchor tags when `to` is used. Pass `_blank`
     * to open in a new tab.
     */
    target: {
      type: String,
      default: null
    },
    to: {
      type: [Object, String],
      default: null
    },
    tooltip: {
      type: String,
      default: null
    },
    tooltipPosition: {
      type: String,
      default: null
    },
    justify: {
      type: String,
      default: () => null
    },
    rounded: Boolean,

    /** Like fullWidth, but with a max width */
    wide: Boolean
  },

  setup(props, {
    emit,
    listeners
  }) {
    // Need to define `h`. Auto injection is disabled as it was conflicting
    // with our manual usage. TODO: Determine better strategy.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const h$1 = h;
    const slots = useSlots();
    const routeToLoading = ref(false);
    const isLoading = computed(() => (routeToLoading.value || props.loading) && !props.disabled);
    const rootClassNames = computed(() => {
      const names = [];
      if (props.fullWidth) names.push('ButtonComponent--full-width');
      if (props.wide) names.push('ButtonComponent--wide');
      return names;
    });
    /**
     * Checks the slot content to determine if the button has content, so that
     * icons set via props are centered in buttons with no content. Whitespace
     * and newlines ignored.
     */

    const getSlotContainsContent = vNodes => {
      var _a, _b;

      if (!vNodes) return false;
      if (vNodes.length > 1) return true;
      const onlyNode = vNodes[0];
      if (!onlyNode) return false;
      if (onlyNode.tag || ((_a = onlyNode.children) === null || _a === void 0 ? void 0 : _a.length)) return true;
      const textWithoutNewlinesAndWhiteSpace = (_b = onlyNode.text) === null || _b === void 0 ? void 0 : _b.replace(/\n/g, '').trim();
      return !!textWithoutNewlinesAndWhiteSpace;
    };

    const buttonClassNames = computed(() => {
      var _a;

      const names = []; // Styles

      switch (props.buttonStyle) {
        case ButtonStyle.PRIMARY:
          names.push('ButtonComponent__button--primary');
          break;

        case ButtonStyle.SUCCESS:
          names.push('ButtonComponent__button--success');
          break;

        case ButtonStyle.OUTLINE:
          names.push('ButtonComponent__button--outline');
          break;

        case ButtonStyle.OUTLINE_DANGER:
          names.push('ButtonComponent__button--outline-danger');
          break;

        case ButtonStyle.OUTLINE_SUCCESS:
          names.push('ButtonComponent__button--outline-success');
          break;

        case ButtonStyle.OUTLINE_GREY:
          names.push('ButtonComponent__button--outline-grey');
          break;

        case ButtonStyle.OUTLINE_BLACK:
          names.push('ButtonComponent__button--outline-black');
          break;

        case ButtonStyle.FROSTED:
          names.push('ButtonComponent__button--frosted');
          break;

        case ButtonStyle.WHITE:
          names.push('ButtonComponent__button--white');
          break;
        // TODO: consider renaming. Other outline styles have transparent
        // background.

        case ButtonStyle.OUTLINE_WHITE:
          names.push('ButtonComponent__button--outline-white');
          break;

        case ButtonStyle.GREY:
          names.push('ButtonComponent__button--grey');
          break;

        case ButtonStyle.IMAGE:
          names.push('ButtonComponent__button--image');
          break;

        case ButtonStyle.LINK:
          names.push('ButtonComponent__button--link');
          names.push('ButtonComponent__button--link-primary');
          break;

        case ButtonStyle.LINK_BLACK:
          names.push('ButtonComponent__button--link');
          names.push('ButtonComponent__button--link-black');
          break;

        case ButtonStyle.LINK_BLACK_ALT:
          names.push('ButtonComponent__button--link');
          names.push('ButtonComponent__button--link-black-alt');
          break;

        case ButtonStyle.LINK_DANGER:
          names.push('ButtonComponent__button--link');
          names.push('ButtonComponent__button--link-danger');
          break;

        case ButtonStyle.LINK_SUCCESS:
          names.push('ButtonComponent__button--link');
          names.push('ButtonComponent__button--link-success');
          break;

        case ButtonStyle.LINK_LIGHT_GREY:
          names.push('ButtonComponent__button--link');
          names.push('ButtonComponent__button--link-light-grey');
          break;

        case ButtonStyle.GHOST:
          names.push('ButtonComponent__button--ghost');
          break;

        case ButtonStyle.GHOST_DANGER:
          names.push('ButtonComponent__button--ghost-danger');
          break;

        case ButtonStyle.GHOST_SUCCESS:
          names.push('ButtonComponent__button--ghost-success');
          break;

        case ButtonStyle.GHOST_BLACK:
          names.push('ButtonComponent__button--ghost-black');
          break;

        case ButtonStyle.DANGER:
          names.push('ButtonComponent__button--danger');
          break;

        case ButtonStyle.TRANSPARENT:
          names.push('ButtonComponent__button--transparent');
          break;

        case ButtonStyle.FROSTED_GREY:
          names.push('ButtonComponent__button--frosted-grey');
          break;
      } // Sizes


      switch (props.buttonSize) {
        case 'sm':
          names.push('ButtonComponent__button--sm');
          break;

        case 'md':
          names.push('ButtonComponent__button--md');
          break;

        case 'lg':
          names.push('ButtonComponent__button--lg');
      }

      if (props.fullWidth) names.push('ButtonComponent__button--full-width');
      if (props.wide) names.push('ButtonComponent__button--wide');
      if (props.circular) names.push('ButtonComponent__button--circular');
      if (props.rounded) names.push('ButtonComponent__button--rounded');
      if (props.square) names.push('ButtonComponent__button--square');

      if (!getSlotContainsContent((_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots))) {
        names.push('ButtonComponent__button--no-content');
      } // States


      if (isLoading.value) names.push('ButtonComponent__button--loading');
      if (props.selected) names.push('ButtonComponent__button--selected');
      return names;
    }); // Decide which hover-animation class to add to the button

    const buttonAnimationClassName = computed(() => {
      if (props.circular) return undefined;

      switch (props.iconAnimation) {
        case ButtonIconAnimation.RIGHT:
          return 'ButtonComponent__button--icon-slide-right';

        case ButtonIconAnimation.LEFT:
          return 'ButtonComponent__button--icon-slide-left';

        case ButtonIconAnimation.DOWN:
          return 'ButtonComponent__button--icon-slide-down';

        case ButtonIconAnimation.UP:
          return 'ButtonComponent__button--icon-slide-up';

        case ButtonIconAnimation.UP_LEFT:
          return 'ButtonComponent__button--icon-slide-up-left';

        case ButtonIconAnimation.UP_RIGHT:
          return 'ButtonComponent__button--icon-slide-up-right';

        case ButtonIconAnimation.DOWN_LEFT:
          return 'ButtonComponent__button--icon-slide-down-left';

        case ButtonIconAnimation.DOWN_RIGHT:
          return 'ButtonComponent__button--icon-slide-down-right';

        default:
          return undefined;
      }
    });
    const tag = computed(() => {
      if (props.disabled) return 'button';
      if (props.to) return 'router-link';
      if (props.href) return 'a';
      return 'button';
    });
    const target = computed(() => {
      if (props.href) return '_blank';
      return props.target;
    });
    const computedTooltip = computed(() => {
      if (typeof props.disabled === 'string') return props.disabled;
      return props.tooltip;
    });
    const buttonStyles = computed(() => {
      const styles = {};

      if (props.customStyles) {
        return props.customStyles;
      }

      if (props.justify) {
        styles.justifyContent = props.justify;
      }

      return styles;
    });
    return () => {
      var _a, _b;

      const classNamesForButton = ['ButtonComponent__button', buttonAnimationClassName.value, ...buttonClassNames.value];
      const classNamesForRoot = ['ButtonComponent', ...rootClassNames.value];
      const buttonIsRoot = !props.tooltip; // When button is not wrapped, there is only one element. Both
      // `ButtonComponent` and `ButtonComponent__button` will be at the root.

      if (buttonIsRoot) classNamesForButton.unshift(...classNamesForRoot);
      const currentRoute = getRoute();

      const clickHandler = event => {
        if (listeners.click) emit('click', event);
        /**
         * In the event that a router-link is clicked, show a loading animation
         * until the route changes. Fall back to stopping loading after 3
         * seconds.
         */

        if (!isNullish(props.to)) {
          routeToLoading.value = true;
          watchOnce(currentRoute, () => {
            routeToLoading.value = false;
          }, {
            deep: true
          });
          setTimeout(() => {
            if (routeToLoading.value) {
              routeToLoading.value = false;
            }
          }, 3000);
        }
      };
      /** Providing an invisible overlay allows for both click and router-link behavior */


      const invisibleOverlay = h$1("div", {
        "class": "ButtonComponent__invisible-overlay",
        "on": {
          "click": clickHandler
        }
      });
      const button = h$1(tag.value, {
        class: classNamesForButton,
        attrs: {
          disabled: !!props.disabled,
          ...(props.href ? {
            href: props.href
          } : {}),
          ...(props.href ? {
            ref: 'noopener noreferrer'
          } : {}),
          type: tag.value === 'button' ? props.nativeType : undefined,
          target: target.value
        },
        props: {
          to: props.to
        },
        style: buttonStyles.value,
        on: {
          click: event => emit('click', event),
          blur: event => emit('blur', event)
        }
      }, [props.iconLeft && h('b-icon', {
        props: {
          pack: props.iconPack,
          icon: props.iconLeft,
          size: 'is-small'
        },
        class: 'ButtonComponent__icon-left'
      }), h('span', {}, (_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots)), props.to ? invisibleOverlay : null, props.iconRight && h('b-icon', {
        props: {
          pack: props.iconPack,
          icon: props.iconRight,
          size: 'is-small'
        }
      })].filter(nullishFilter));
      return computedTooltip.value ? h$1(__vue_component__, {
        "class": classNamesForRoot,
        "attrs": {
          "alwaysAllowOnTouchDevices": props.alwaysAllowTooltipOnTouchDevices,
          "label": computedTooltip.value,
          "position": (_b = props.tooltipPosition) !== null && _b !== void 0 ? _b : 'bottom',
          "forceWrap": true
        }
      }, [button]) : button;
    };
  }

});

export { controller };
